const PictViewClass = require('pict-view');
const HeadlightSampleScanningBundle = require('headlight-sample-scanning');

/** @typedef {import('./HeadlightPanels-View.js').Pict} Pict */

/**
 * The panels' left-hand "Scan Samples" panel: the panel chrome, the camera and label printing. Everything else
 * (location, settings, moves, lists, and taking scans from the app's HeadlightQR provider while open) is the
 * SampleScanning provider and view from headlight-sample-scanning, which any app can mount without the panels.
 */
class QRPanel extends PictViewClass
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
		this.cameraOpen = false;
		this.toggleRunning = false;
		/** @type {boolean|undefined} the switch setting asked for by open(), applied when the panel draws */
		this.pendingSaveEachScan = undefined;
		this.scanningOptions = { SampleScanning: { RemoveFromCurrentLocation: pOptions.removeFromCurrentLocation ?? true } };
		/** @type {Array<(pMove: Record<string, any>) => any>} */
		this.movedListeners = [];
	}

	/** @return {any} the SampleScanning provider, attached on first use */
	get scanning()
	{
		return new HeadlightSampleScanningBundle(this.pict).attach(this.scanningOptions);
	}

	/**
	 * Lifecycle hook that triggers after the view is rendered.
	 *
	 * @param {import('pict-view').Renderable} pRenderable - The renderable that was rendered.
	 */
	onAfterRender(pRenderable)
	{
		if (this.cameraOpen)
		{
			// The camera's box was just redrawn.
			this.cameraOpen = false;
			this.pict.providers.QRCodeProvider.closeScanner().catch((e) =>
			{
				console.log('Error stopping qr code scanner: ', e);
			});
		}
		const tmpScanning = this.scanning;
		tmpScanning.connect(
			{
				notify: (pToast) => this.pict.views.HeadlightPanels.toast(pToast),
				printLabel: (pSample) => this.printEntityLabel('Sample', pSample.QRCode),
				moved: (pMove) => this.reportMoved(pMove),
				// Samples marked received changed status, so a page showing one refreshes as it does for a move.
				received: (pMark) => this.reportMoved({ Location: null, Moved: pMark.Received, Failed: pMark.Failed, Via: 'received' }),
				camera: { toggle: (pContainerID) => this.toggleCamera(pContainerID), isOpen: () => this.cameraOpen },
			});
		tmpScanning.open({ SaveEachScan: this.pendingSaveEachScan, DestinationAddress: '#QRPanel-Body' });
		// The panels nav opens this pane by rendering it, not through open(); take the scanner either way.
		tmpScanning.listen(this.pict.providers.HeadlightQR);
		this.pendingSaveEachScan = undefined;
		return super.onAfterRender(pRenderable);
	}

	/**
	 * Opens the scanner in the left hand panel.
	 * @param {'putaway'|'receive'} [pMode] - 'receive' turns on saving each scan, 'putaway' turns it off; left out,
	 *   the setting stays as this device last had it.
	 */
	open(pMode)
	{
		this.pendingSaveEachScan = (pMode === 'receive') ? true : ((pMode === 'putaway') ? false : undefined);
		this.pict.views.HeadlightPanels.handlePanelToggle('QRPanel', 'open');
		this.pict.views.HeadlightPanels.setPanelCustomWidth('left', 40);
	}

	closeHandler()
	{
		console.log('Closing QR Code Panel.');
		this.scanning.close();
		this.cameraOpen = false;
		this.pict.providers.QRCodeProvider.closeScanner();
	}

	/**
	 * Hear about moves made from this panel, e.g. so a page showing one of the moved samples can refresh. Samples marked
	 * received come through here too (Via 'received', Moved = the samples marked, no Location).
	 * @param {(pMove: { Location: Record<string, any>|null, Moved: Array<Record<string, any>>, Failed: Array<Record<string, any>>, Via?: string }) => any} fListener
	 * @return {() => void} removes the listener
	 */
	addMovedListener(fListener)
	{
		this.movedListeners.push(fListener);
		return () =>
		{
			const tmpIndex = this.movedListeners.indexOf(fListener);
			if (tmpIndex >= 0)
			{
				this.movedListeners.splice(tmpIndex, 1);
			}
		};
	}

	/**
	 * Pass a move on to every listener; one that fails is logged and doesn't stop the others.
	 * @param {{ Location: Record<string, any>, Moved: Array<Record<string, any>>, Failed: Array<Record<string, any>> }} pMove
	 * @return {Promise<void>}
	 */
	async reportMoved(pMove)
	{
		for (const fListener of this.movedListeners.slice())
		{
			try
			{
				await fListener(pMove);
			}
			catch (pError)
			{
				this.log.error(`Scan Samples panel: a move listener failed: ${(pError && pError.message) || pError}`);
			}
		}
	}

	/**
	 * Opens or closes the camera scanner in the given container.
	 * @param {string} pContainerID
	 * @return {Promise<boolean>} whether the camera is now open
	 */
	async toggleCamera(pContainerID)
	{
		// Don't run this function again until the ui transition is finished.
		if (this.toggleRunning)
		{
			return this.cameraOpen;
		}
		this.toggleRunning = true;
		this.cameraOpen = !this.cameraOpen;
		try
		{
			if (this.cameraOpen)
			{
				await this.pict.providers.QRCodeProvider.openScanner(pContainerID, { isPhoneDevice: this.pict.services.HeadlightPanels.isPhoneDevice });
			}
			else
			{
				await this.pict.providers.QRCodeProvider.closeScanner();
			}
		}
		catch (e)
		{
			console.log('Error toggling the qr code scanner: ', e);
		}
		this.toggleRunning = false;
		return this.cameraOpen;
	}

	/**
	 * This method is used to handle the closing of the panel.
	 * @param {Event} [event] - The click event
	 * @return {void}
	 */
	handleClosePanelEvent(event)
	{
		this.pict.views.HeadlightPanels.closeAllUnpinnedPanels();
	}

	/**
	 * Print an entity's QR label, loading the customer's label configuration first.
	 *
	 * Without the ensure these buttons print the built-in fallback layout: the panels app never loaded the
	 * printable manifests, so QRCodeProvider.typeConfig was empty and per-customer printer configuration
	 * (PrintStyle, title, field rows/columns) was silently ignored.
	 * @param {String} pEntity - The entity type being printed ('Sample' | 'Location')
	 * @param {String} pQRCode - The entity's QR code
	 * @return {Promise<void>}
	 * @async
	 */
	async printEntityLabel(pEntity, pQRCode)
	{
		if (!pQRCode)
		{
			return;
		}
		await this.pict.providers.QRCodeProvider.ensurePrintableManifests();
		const tmpKnown = (pEntity === 'Sample') ? this.scanning.samples.find((pSample) => pSample.QRCode === pQRCode) : null;
		this.pict.providers.QRCodeProvider.printDecoratedEntity(pEntity, pQRCode, undefined, undefined, tmpKnown);
	}
}

module.exports = QRPanel;

module.exports.default_configuration =
{
	ViewIdentifier: 'QRPanel',
	DefaultRenderable: 'QRPanel',
	DefaultDestinationAddress: '#HeadlightPanels-LeftPanel',
	Templates:
	[
		{
			Hash: 'QRPanel-Wrapper',
			Template: /*html*/`
				<article id="HeadlightPanels-QRPanel" class="panel is-active">
					<style>
						#HeadlightPanels-QRPanel
						{
							background-color: var(--panels-background-color);
						}
						/* The Scan Samples cart in the panels' dark palette, like the other panels. */
						#HeadlightPanels-QRPanel .hl-scan
						{
							--hl-scan-background: var(--panels-background-color);
							--hl-scan-text: var(--panels-content-block-text-color);
							--hl-scan-text-strong: var(--panels-label-color);
							--hl-scan-text-weak: var(--panels-content-block-type-icon-color);
							--hl-scan-border: var(--panels-content-block-divider-color);
							--hl-scan-border-weak: var(--panels-content-block-divider-color);
							--hl-scan-head-background: var(--panels-tabs-background-color);
							--hl-scan-input-background: var(--panels-input-background-color);
							--hl-scan-input-text: var(--panels-input-text-color);
							--hl-scan-input-border: var(--panels-input-border-color);
						}
						#QRPanel-Body
						{
							max-height: calc(100vh - var(--panels-header-height) - var(--panels-navbar-height)) !important;
							overflow-y: auto !important;
						}
					</style>
					<p id="HeadlightPanels-QRPanel-Header" class="panel-heading pr-4">
						<button type="button" class="is-pulled-right hl-panel-close-button" aria-label="close" title="Close panel" 
							onclick="_Pict.views.QRPanel.handleClosePanelEvent(event)">
							<span class="icon">
								<i class="fas fa-fw fa-times-circle" aria-hidden="true"></i>
							</span>
						</button>
						<button type="button" class="is-pulled-right hl-panel-pin-button mr-2" aria-label="pin" title="Pin content" onclick="_Pict.views.HeadlightPanels.togglePanelPinState()">
							<span class="icon">
								<i class="fas fa-fw fa-thumbtack" aria-hidden="true"></i>
							</span>
						</button>
						<span class="icon">
							<i id="HeadlightPanels-QRPanel-Header-Icon" class="fas fa-fw fa-qrcode" aria-hidden="true"></i>
						</span>
						<span id="HeadlightPanels-QRPanel-Header-Title" class="mr-3">Scan Samples</span>
					</p>
					<div id="QRPanel-Body" class="panel-content panel-has-nested-scroll-content"></div>
				</article>
			`,
		},
	],
	Renderables:
	[
		{
			RenderableHash: 'QRPanel',
			TemplateHash: 'QRPanel-Wrapper',
			DestinationAddress: '#HeadlightPanels-LeftPanel',
			RenderType: 'replace',
		},
	],
};
